import React from 'react';
import { Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// PDF Export Button Component using jsPDF
export default function ExportPDFSimple({ data, weekInfo, reportingRole, loginRole, disabled = false }) {
    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);

    const getPLDetails = (record) => {
        let bookedPL;
        let brokerage;
        let grossPL;

        if (reportingRole === 'admin') {
            bookedPL = record?.totalBookedProfitLoss?.admin || 0;
            brokerage = record?.totalBrokerage?.admin || 0;
            grossPL = record?.totalGrossProfitLoss?.admin || 0;
        } else if (reportingRole === 'broker') {
            bookedPL = record?.totalBookedProfitLoss?.broker || 0;
            brokerage = record?.totalBrokerage?.broker || 0;
            grossPL = record?.totalGrossProfitLoss?.broker || 0;
        } else {
            bookedPL = record?.totalBookedProfitLoss?.client || 0;
            brokerage = record?.totalBrokerage?.client || 0;
            grossPL = record?.totalGrossProfitLoss?.client || 0;
        }
        return { bookedPL, brokerage, grossPL };
    }; const handleExportPDF = () => {
        try {
            // eslint-disable-next-line new-cap
            const doc = new jsPDF('landscape');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // Title
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(25, 118, 210); // Blue color
            doc.text('P/L SUMMARY REPORT', pageWidth / 2, 20, { align: 'center' });

            // Subtitle
            doc.setFontSize(14);
            doc.setTextColor(102, 102, 102); // Gray color
            doc.text(`${reportingRole.toUpperCase()}S P/L DATA`, pageWidth / 2, 30, { align: 'center' });

            // Week info
            doc.setFontSize(12);
            doc.setTextColor(51, 51, 51); // Dark gray
            doc.text(`Settlement Week: ${weekInfo}`, pageWidth / 2, 40, { align: 'center' });

            // Prepare table data
            const shouldShowBrokerColumn = loginRole !== 'broker' && reportingRole === 'client';
            const shouldShowAdminColumn = loginRole === 'superAdmin' && reportingRole === 'client';

            // Table headers
            const headers = ['USERNAME', 'P&L', 'BROKERAGE', 'FINAL P&L'];

            if (shouldShowBrokerColumn) {
                headers.push('BROKER');
            }
            if (shouldShowAdminColumn) {
                headers.push('ADMIN');
            }

            // Table rows
            const tableRows = [];
            let totalBookedPL = 0;
            let totalBrokerage = 0;
            let totalGrossPL = 0;

            data.forEach((record) => {
                const { bookedPL, brokerage, grossPL } = getPLDetails(record);

                totalBookedPL += bookedPL;
                totalBrokerage += brokerage;
                totalGrossPL += grossPL;

                const userRefName = (() => {
                    if (record?.creator?.firstname !== '' && record?.creator?.lastname !== '') {
                        return `(${record.creator.firstname} ${record.creator.lastname})`;
                    }
                    if (record?.creator?.lastname !== '') {
                        return `(${record.creator.lastname})`;
                    }
                    if (record?.creator?.firstname !== '') {
                        return `(${record.creator.firstname})`;
                    }
                    return '';
                })();

                const row = [
                    `${record?.creator?.username?.toUpperCase()} ${userRefName}` || 'N/A',
                    formatCurrency(bookedPL),
                    formatCurrency(brokerage),
                    formatCurrency(grossPL),
                ];

                if (shouldShowBrokerColumn) {
                    const brokerRefName = (() => {
                        if (record?.broker?.firstname !== '' && record?.broker?.lastname !== '') {
                            return `(${record?.broker?.firstname} ${record.broker.lastname})`;
                        }
                        if (record?.broker?.lastname !== '') {
                            return `(${record?.broker?.lastname})`;
                        }
                        if (record?.broker?.firstname !== '') {
                            return `(${record?.broker?.firstname})`;
                        }
                        return '';
                    })();
                    row.push(`${record?.broker?.username?.toUpperCase()} ${brokerRefName}` || 'N/A');
                }

                if (shouldShowAdminColumn) {
                    const adminRefName = (() => {
                        if (record?.admin?.firstname !== '' && record?.admin?.lastname !== '') {
                            return `(${record?.admin?.firstname} ${record.admin.lastname})`;
                        }
                        if (record?.admin?.lastname !== '') {
                            return `(${record?.admin?.lastname})`;
                        }
                        if (record?.admin?.firstname !== '') {
                            return `(${record?.admin?.firstname})`;
                        }
                        return '';
                    })();
                    row.push(`${record?.admin?.username?.toUpperCase()} ${adminRefName}` || 'N/A');
                }

                tableRows.push(row);
            });

            // Add total row
            if (data.length > 0) {
                const totalRow = [
                    'TOTAL',
                    formatCurrency(totalBookedPL),
                    formatCurrency(totalBrokerage),
                    formatCurrency(totalGrossPL),
                ];

                if (shouldShowBrokerColumn) {
                    totalRow.push('-');
                }
                if (shouldShowAdminColumn) {
                    totalRow.push('-');
                }

                tableRows.push(totalRow);
            }

            // Create table
            doc.autoTable({
                head: [headers],
                body: tableRows,
                startY: 50,
                theme: 'grid',
                headStyles: {
                    fillColor: [33, 150, 243], // Blue header
                    textColor: [255, 255, 255], // White text
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'center',
                },
                bodyStyles: {
                    fontSize: 9,
                    halign: 'center',
                },
                alternateRowStyles: {
                    fillColor: [248, 248, 248], // Light gray
                },
                didDrawCell: (data) => {
                    // Color code P&L values
                    if (data.column.index === 1 || data.column.index === 3) { // P&L and Final P&L columns
                        const cellValue = data.cell.raw;
                        if (typeof cellValue === 'string' && cellValue !== 'P&L' && cellValue !== 'FINAL P&L' && cellValue !== 'TOTAL') {
                            const numValue = parseFloat(cellValue.replace(/[^\d.-]/g, ''));
                            if (numValue > 0) {
                                data.cell.styles.textColor = [76, 175, 80]; // Green
                            } else if (numValue < 0) {
                                data.cell.styles.textColor = [244, 67, 54]; // Red
                            } else {
                                data.cell.styles.textColor = [33, 150, 243]; // Blue
                            }
                        }
                    }
                    // Brokerage column - always blue
                    if (data.column.index === 2 && data.cell.raw !== 'BROKERAGE') {
                        data.cell.styles.textColor = [33, 150, 243]; // Blue
                    }
                },
                didParseCell: (data) => {
                    // Style total row
                    if (data.row.index === tableRows.length - 1) {
                        data.cell.styles.fillColor = [224, 224, 224]; // Light gray background
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fontSize = 10;
                    }
                },
                margin: { top: 50, left: 10, right: 10 },
                tableWidth: 'auto',
                columnStyles: {
                    0: { cellWidth: 'auto', minCellWidth: 30 }, // USERNAME
                    1: { cellWidth: 'auto', minCellWidth: 25 }, // P&L
                    2: { cellWidth: 'auto', minCellWidth: 25 }, // BROKERAGE
                    3: { cellWidth: 'auto', minCellWidth: 25 }, // FINAL P&L
                },
            });

            // Footer
            const finalY = doc.lastAutoTable.finalY || 50;
            doc.setFontSize(10);
            doc.setTextColor(102, 102, 102);
            doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 10, pageHeight - 20);
            doc.text(`P/L Summary Report - ${reportingRole.toUpperCase()}S`, pageWidth - 10, pageHeight - 20, { align: 'right' });

            // Save the PDF
            const fileName = `PL_Summary_${reportingRole}_${weekInfo.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    return (
        <Button
            variant="contained"
            color="error"
            startIcon={<DownloadIcon />}
            onClick={handleExportPDF}
            disabled={disabled || !data || data.length === 0}
            sx={{
                minWidth: 140,
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                    backgroundColor: '#d32f2f',
                },
            }}
        >
            Export PDF
        </Button>
    );
}
